export const SERVICES = [
  {
    slug: "it-strategy-roadmapping",
    whyNow:
      "Every quarter without a roadmap is another quarter of reactive spending. Tool sprawl compounds, contracts renew on autopilot, and the gap between where you are and where you planned to be widens — quietly.",
    name: "IT Strategy & Roadmapping",
    icon: "Map",
    positioning: "Move from reactive technology decisions to a practical roadmap.",
    cta: "Build Your Technology Roadmap",
    summary:
      "We turn scattered technology priorities into a clear, sequenced roadmap tied to your business goals and budget.",
    whoFor:
      "Leaders making technology decisions one crisis at a time who need a deliberate plan for the next 12 to 36 months.",
    problem:
      "Without a strategy, technology decisions are reactive — tools accumulate, costs overlap, and leadership lacks a shared view of what's coming and why. The result is wasted spend and missed priorities.",
    explanation:
      "We assess your current technology footprint, align priorities to your business objectives, and sequence initiatives into a practical roadmap. Strategy comes first; tools come second.",
    problemsSolved: [
      "Reactive, crisis-driven technology decisions",
      "Overlapping tools and unclear technology spend",
      "Competing initiatives with no sequencing",
      "No shared plan between leadership and IT",
    ],
    helps: [
      "Assess current technology footprint and spend",
      "Align priorities to business objectives",
      "Sequence initiatives by value and dependency",
      "Establish a budget-aligned 12–36 month roadmap",
    ],
    deliverables: [
      "Technology strategy and roadmap",
      "Initiative prioritization framework",
      "Current-state assessment",
      "Executive briefing",
    ],
    benefits: [
      "A clear, shared plan for the future",
      "Better budgeting and fewer surprises",
      "Proactive instead of reactive decisions",
    ],
    phase: "Align",
    related: ["business-analysis", "fractional-cto", "vendor-selection"],
  },
  {
    slug: "business-analysis",
    whyNow:
      "The cheapest time to fix a technology project is before it starts. Once contracts are signed and the build begins, every unclear requirement turns into change orders, rework, and delay.",
    name: "Business Analysis",
    icon: "ClipboardList",
    positioning: "Better technology outcomes begin with better business analysis.",
    cta: "Discuss Your Business Analysis Needs",
    summary:
      "We translate business goals into clear, validated requirements so technology investments solve the right problems.",
    whoFor:
      "Teams about to invest in new systems or processes who need clarity before committing budget.",
    problem:
      "Most failed technology projects fail before a single line of code is written — unclear goals, undocumented processes, and untested assumptions lead to tools that don't match how the business operates.",
    explanation:
      "Our business analysis engagements map current-state processes, surface inefficiencies, and define the precise requirements a successful solution must meet — in the language of operations and outcomes.",
    problemsSolved: [
      "Unclear or conflicting project requirements",
      "Processes that live in people's heads",
      "Technology purchases that don't fit real workflows",
      "Scope creep and costly rework",
    ],
    helps: [
      "Facilitated discovery with the people who do the work",
      "Current-state and future-state process mapping",
      "Prioritized, validated requirements",
      "A clear link between each requirement and an outcome",
    ],
    deliverables: [
      "Process maps (current and future state)",
      "Requirements specification",
      "Gap analysis",
      "Prioritized recommendations",
    ],
    benefits: [
      "Fewer surprises and less rework",
      "Technology that fits the business",
      "A shared, documented understanding",
    ],
    phase: "Analyze",
    related: ["it-strategy-roadmapping", "systems-integration", "vendor-selection"],
  },
  {
    slug: "cybersecurity-assessment",
    whyNow:
      "Attackers don't wait until you're ready — and neither do cyber-insurance renewals or client security questionnaires. Knowing your real exposure now costs far less than discovering it during an incident.",
    name: "Cybersecurity Assessment",
    icon: "ShieldCheck",
    positioning: "Cybersecurity is a business risk, not just an IT issue.",
    cta: "Request a Cybersecurity Assessment",
    summary:
      "We help leaders understand and reduce cyber risk in practical business terms — without fear or jargon.",
    whoFor:
      "Executives who need a clear, honest picture of their exposure and a practical plan to reduce it.",
    problem:
      "Cybersecurity is often treated as a purely technical concern, leaving leaders unsure of their real exposure. Risks go unaddressed while budgets are spent on tools that don't match the threat.",
    explanation:
      "We translate cyber risk into business language. We assess where you are genuinely exposed, prioritize the protections that matter most for your size, and give leadership a calm, clear picture of what to do next.",
    problemsSolved: [
      "Uncertainty about real cyber exposure",
      "Security treated as 'IT's problem'",
      "Compliance and client security requirements",
      "Spending on tools that don't reduce real risk",
    ],
    helps: [
      "Assess current security posture and exposure",
      "Prioritize protections by business impact",
      "Translate risk for executives and boards",
      "Define a practical remediation roadmap",
    ],
    deliverables: [
      "Security posture assessment",
      "Risk-prioritized recommendations",
      "Executive risk summary",
      "Remediation roadmap",
    ],
    benefits: [
      "A clear understanding of real exposure",
      "Protection focused on what matters most",
      "Confidence in client and compliance conversations",
    ],
    phase: "Sustain",
    related: ["it-strategy-roadmapping", "cloud-infrastructure", "fractional-cto"],
  },
  {
    slug: "systems-integration",
    whyNow:
      "Manual re-keying grows with every new customer and hire. The longer systems stay disconnected, the more workarounds harden into 'the way we do things' — and the harder they are to unwind.",
    name: "Systems Integration Consulting",
    icon: "Network",
    positioning: "Disconnected systems create operational drag.",
    cta: "Discuss Systems Integration",
    summary:
      "We plan how your systems connect so data flows cleanly and your team stops re-keying information.",
    whoFor:
      "Organizations running multiple disconnected tools that create manual work, errors, and reporting delays.",
    problem:
      "When systems don't talk to each other, staff re-enter data, errors multiply, and reporting is slow and unreliable. Integration is often attempted ad hoc, creating fragile connections that break.",
    explanation:
      "We map how information should move across your business, define the right integration approach, and create a practical plan to connect your systems reliably — reducing manual work and improving data quality.",
    problemsSolved: [
      "Manual data re-entry between systems",
      "Inconsistent or conflicting data",
      "Slow, unreliable reporting",
      "Fragile, ad-hoc integrations that break",
    ],
    helps: [
      "Map data flows across the business",
      "Define the right integration architecture",
      "Plan reliable, maintainable connections",
      "Prioritize integrations by business value",
    ],
    deliverables: [
      "Integration architecture plan",
      "Data flow mapping",
      "Prioritized integration roadmap",
      "Vendor and tooling recommendations",
    ],
    benefits: [
      "Less manual work and fewer errors",
      "Cleaner, more trustworthy data",
      "Faster, more reliable reporting",
    ],
    phase: "Transform",
    related: ["business-analysis", "data-reporting-bi", "software-modernization"],
  },
  {
    slug: "software-modernization",
    whyNow:
      "Legacy systems rarely fail on a convenient schedule. Vendor support ends, key staff leave, and security gaps widen — and an unplanned replacement costs far more than a planned one.",
    name: "Software Modernization Planning",
    icon: "RefreshCw",
    positioning: "Legacy systems should be evaluated before they become business constraints.",
    cta: "Plan a Modernization Strategy",
    summary:
      "We evaluate aging systems and define a deliberate, low-risk path to modernize what matters most.",
    whoFor:
      "Businesses constrained by legacy software that limits growth, security, reporting, or vendor flexibility.",
    problem:
      "Legacy systems quietly restrict growth — limiting security, reporting, and the ability to change vendors. Replacing them all at once is risky, but doing nothing is riskier still.",
    explanation:
      "We assess your current systems, weigh the risks of staying versus modernizing, and define a phased plan that prioritizes the highest-value changes while minimizing disruption.",
    problemsSolved: [
      "Aging systems limiting growth and security",
      "Vendor lock-in and limited flexibility",
      "Poor reporting from legacy platforms",
      "Risky 'big bang' replacement attempts",
    ],
    helps: [
      "Assess legacy systems and associated risk",
      "Weigh modernization options and trade-offs",
      "Define a phased, low-risk plan",
      "Sequence changes by business value",
    ],
    deliverables: [
      "Legacy system assessment",
      "Modernization options analysis",
      "Phased modernization plan",
      "Risk and dependency mapping",
    ],
    benefits: [
      "Modernization without operational chaos",
      "Reduced security and vendor risk",
      "A clear, value-led path forward",
    ],
    phase: "Transform",
    related: ["it-strategy-roadmapping", "systems-integration", "project-leadership"],
  },
  {
    slug: "cloud-infrastructure",
    whyNow:
      "Aging servers and ad-hoc cloud decisions get more expensive and riskier with every renewal cycle. Planning the move on your timeline beats scrambling after a hardware failure.",
    name: "Cloud & Infrastructure Consulting",
    icon: "Cloud",
    positioning: "Cloud decisions should support reliability, security, cost control, and business needs.",
    cta: "Review Your Infrastructure Strategy",
    summary:
      "We help you make cloud and infrastructure decisions that balance reliability, security, and cost.",
    whoFor:
      "Leaders unsure whether their infrastructure choices are reliable, secure, and cost-effective.",
    problem:
      "Cloud and infrastructure decisions are often made reactively or driven by a single vendor's pitch — leading to surprise costs, reliability gaps, and security blind spots.",
    explanation:
      "We review your current infrastructure against your business needs and define a strategy that balances reliability, security, and cost — vendor-neutral and grounded in your priorities.",
    problemsSolved: [
      "Surprise or runaway cloud costs",
      "Reliability and uptime concerns",
      "Security gaps in infrastructure",
      "Vendor-driven, reactive decisions",
    ],
    helps: [
      "Review current infrastructure and costs",
      "Align infrastructure to business needs",
      "Balance reliability, security, and cost",
      "Provide vendor-neutral recommendations",
    ],
    deliverables: [
      "Infrastructure assessment",
      "Cost and reliability analysis",
      "Recommended infrastructure strategy",
      "Migration or optimization plan",
    ],
    benefits: [
      "Predictable, controlled costs",
      "Stronger reliability and security",
      "Confidence in infrastructure decisions",
    ],
    phase: "Align",
    related: ["cybersecurity-assessment", "it-strategy-roadmapping", "systems-integration"],
  },
  {
    slug: "business-process-automation",
    whyNow:
      "Every month of manual process is payroll spent on work software should be doing. Teams that automate first compound the savings — and free their people for higher-value work.",
    name: "Business Process Automation",
    icon: "Cog",
    positioning: "Automation should remove friction from real business workflows.",
    cta: "Identify Automation Opportunities",
    summary:
      "We identify where automation genuinely removes friction — saving time without adding complexity.",
    whoFor:
      "Teams bogged down by repetitive manual work who want practical, high-value automation.",
    problem:
      "Manual, repetitive tasks drain time and introduce errors. Automation efforts often target the wrong processes or add complexity that creates new problems.",
    explanation:
      "We examine your real workflows, identify the highest-value opportunities to automate, and define a practical plan that removes friction without over-engineering.",
    problemsSolved: [
      "Repetitive manual tasks and busywork",
      "Errors from manual data handling",
      "Slow, friction-heavy workflows",
      "Automation that adds complexity",
    ],
    helps: [
      "Map and review real workflows",
      "Identify high-value automation opportunities",
      "Prioritize by effort and impact",
      "Plan practical, maintainable automation",
    ],
    deliverables: [
      "Workflow assessment",
      "Prioritized automation opportunities",
      "Implementation plan",
      "Tooling recommendations",
    ],
    benefits: [
      "Time saved on repetitive work",
      "Fewer errors and rework",
      "Smoother, faster workflows",
    ],
    phase: "Optimize",
    related: ["business-analysis", "systems-integration", "data-reporting-bi"],
  },
  {
    slug: "data-reporting-bi",
    whyNow:
      "Decisions are being made today on numbers assembled by hand. Every reporting cycle that takes days instead of minutes delays action and hides problems until they're expensive.",
    name: "Data & Reporting / Business Intelligence",
    icon: "BarChart3",
    positioning: "Turn business data into better decisions.",
    cta: "Discuss Reporting Improvements",
    summary:
      "We turn scattered data into clear dashboards and metrics that drive confident decisions.",
    whoFor:
      "Leaders relying on gut feel or manual spreadsheets who need trustworthy, timely reporting.",
    problem:
      "Many organizations sit on valuable data but can't access it when decisions are made. Reports are manual, inconsistent, and slow — so leaders rely on gut feel instead of facts.",
    explanation:
      "We help you define the metrics that matter, connect your data sources, and build reporting that leadership can trust — fewer spreadsheets, faster decisions.",
    problemsSolved: [
      "Manual, time-consuming reporting",
      "Conflicting numbers across departments",
      "Decisions based on gut feel",
      "Data trapped in disconnected systems",
    ],
    helps: [
      "Define meaningful KPIs and metrics",
      "Connect and consolidate data sources",
      "Build clear, trustworthy dashboards",
      "Establish a single source of truth",
    ],
    deliverables: [
      "KPI and metrics framework",
      "Dashboard design and build plan",
      "Data source integration plan",
      "Reporting governance",
    ],
    benefits: [
      "Faster, fact-based decisions",
      "Consistent numbers across the business",
      "Less time wrangling spreadsheets",
    ],
    phase: "Optimize",
    related: ["systems-integration", "business-analysis", "ai-readiness"],
  },
  {
    slug: "vendor-selection",
    whyNow:
      "Most software contracts lock you in for years. A rushed selection made under demo pressure becomes a multi-year constraint — investing weeks up front protects years of spend.",
    name: "Vendor Selection Support",
    icon: "Search",
    positioning: "Choose technology vendors with confidence.",
    cta: "Get Vendor Selection Support",
    summary:
      "We guide you through evaluating and selecting the right technology vendors — objectively and on your terms.",
    whoFor:
      "Teams facing a high-stakes software or partner decision who want an objective process.",
    problem:
      "Selecting software or a technology partner is high-stakes and high-pressure. Sales processes are built to persuade, and it's hard to objectively compare options against what your business actually needs.",
    explanation:
      "We bring an objective, structured process to vendor selection — defining real requirements, running a fair evaluation, and helping you negotiate from clarity so you choose the right fit, not the best pitch.",
    problemsSolved: [
      "Pressure-driven purchasing decisions",
      "Difficulty comparing vendors objectively",
      "Requirements that aren't clearly defined",
      "Unfavorable contract terms",
    ],
    helps: [
      "Define requirements and evaluation criteria",
      "Run a structured, objective comparison",
      "Coordinate demos and reference checks",
      "Support negotiation and contract review",
    ],
    deliverables: [
      "Requirements and scoring criteria",
      "Vendor evaluation matrix",
      "Recommendation summary",
      "Negotiation support",
    ],
    benefits: [
      "Confidence you chose the right fit",
      "Objective, defensible decisions",
      "Better contract terms",
    ],
    phase: "Align",
    related: ["business-analysis", "it-strategy-roadmapping", "project-leadership"],
  },
  {
    slug: "project-leadership",
    whyNow:
      "Struggling projects rarely self-correct — they burn budget while stakeholders lose confidence. The earlier experienced leadership steps in, the more of the original investment can be saved.",
    name: "Project Leadership",
    icon: "ListChecks",
    positioning: "Better project outcomes require structure, visibility, and accountability.",
    cta: "Discuss Project Leadership Support",
    summary:
      "We provide experienced project leadership to keep important initiatives on scope, on schedule, and on budget.",
    whoFor:
      "Organizations with important initiatives that keep stalling against day-to-day demands.",
    problem:
      "Important technology initiatives compete with day-to-day work for attention. Without dedicated leadership, projects drift, deadlines slip, and accountability becomes unclear.",
    explanation:
      "We step in as a steady hand to lead complex initiatives — coordinating vendors, internal teams, and stakeholders with structure, visibility, and disciplined execution so projects actually finish.",
    problemsSolved: [
      "Projects stalling due to competing priorities",
      "Unclear ownership and accountability",
      "Poor vendor and stakeholder coordination",
      "Schedule and budget overruns",
    ],
    helps: [
      "Establish project structure and governance",
      "Coordinate vendors and internal teams",
      "Maintain visibility through clear reporting",
      "Manage risk and remove blockers",
    ],
    deliverables: [
      "Project plan and schedule",
      "Status reporting cadence",
      "Risk and issue tracking",
      "Stakeholder communication plan",
    ],
    benefits: [
      "Initiatives that finish on time and on budget",
      "Clear accountability and reporting",
      "Reduced internal disruption",
    ],
    phase: "Transform",
    related: ["business-analysis", "software-modernization", "vendor-selection"],
  },
  {
    slug: "ai-readiness",
    whyNow:
      "Your competitors are already experimenting with AI — some wisely, some wastefully. Getting your data and governance ready now means you adopt on evidence, not hype, when the right use case appears.",
    name: "AI Readiness & Responsible AI",
    icon: "Sparkles",
    positioning: "Practical AI strategy for real business problems.",
    cta: "Assess Your AI Readiness",
    summary:
      "We help you adopt AI responsibly — focused on measurable value, not hype or risk.",
    whoFor:
      "Leaders under pressure to 'do something with AI' who want a practical, responsible approach.",
    problem:
      "AI is everywhere in the headlines, but most organizations struggle to separate genuine opportunity from noise. Without a clear approach, AI pilots stall, create new risks, or never deliver value.",
    explanation:
      "We bring a practical, business-first lens to AI — identifying where it can realistically improve operations, weighing the risks honestly, and defining a responsible path to adoption that protects your data and reputation.",
    problemsSolved: [
      "Uncertainty about where AI adds value",
      "Data privacy and responsible-use concerns",
      "Pilots that never reach production",
      "Pressure to adopt AI without a plan",
    ],
    helps: [
      "Identify high-value, low-risk use cases",
      "Assess data readiness and governance",
      "Define responsible-use guidelines",
      "Build a phased adoption roadmap",
    ],
    deliverables: [
      "AI readiness assessment",
      "Prioritized use-case shortlist",
      "Responsible-use guidelines",
      "Adoption roadmap",
    ],
    benefits: [
      "Confidence to adopt AI without unnecessary risk",
      "Focus on measurable business value",
      "A defensible, responsible approach",
    ],
    phase: "Discover",
    related: ["it-strategy-roadmapping", "data-reporting-bi", "business-process-automation"],
  },
  {
    slug: "fractional-cto",
    whyNow:
      "Technology decisions made without senior guidance don't wait — they accumulate as cost, risk, and rework. Executive-level judgment costs a fraction of one bad platform decision.",
    name: "Fractional CTO / Technology Advisor",
    icon: "UserCog",
    positioning: "Executive technology leadership without a full-time CTO.",
    cta: "Explore Fractional CTO Services",
    summary:
      "We provide senior technology leadership on a fractional basis — strategic guidance at a fraction of the cost.",
    whoFor:
      "Growing organizations that need executive technology judgment without a full-time hire.",
    problem:
      "Growing organizations often need executive-level technology judgment but cannot justify a full-time CTO — leaving leadership making high-stakes decisions without a trusted technical advisor.",
    explanation:
      "Our fractional CTO engagements give you a senior technology partner who understands your business — guiding strategy, overseeing key initiatives, evaluating vendors, and mentoring internal staff, scaled to your needs.",
    problemsSolved: [
      "No senior technology voice at the table",
      "High-stakes decisions without expert input",
      "Internal IT lacking strategic direction",
      "A full-time executive isn't yet justified",
    ],
    helps: [
      "Serve as a strategic advisor to leadership",
      "Oversee critical initiatives and vendors",
      "Guide and mentor internal IT staff",
      "Bring executive discipline to decisions",
    ],
    deliverables: [
      "Ongoing strategic advisory",
      "Technology decision support",
      "Vendor and initiative oversight",
      "Leadership-level reporting",
    ],
    benefits: [
      "Executive technology leadership on demand",
      "Better decisions at a fraction of the cost",
      "A trusted advisor invested in your outcomes",
    ],
    phase: "Sustain",
    related: ["it-strategy-roadmapping", "cybersecurity-assessment", "cloud-infrastructure"],
  },
];

export const getService = (slug) => SERVICES.find((s) => s.slug === slug);

export const SERVICE_PILLARS = [
  {
    slug: "strategy-leadership",
    name: "Strategy & Leadership",
    icon: "Map",
    summary: "Executive-level direction for your technology decisions — without the full-time hire.",
    services: ["it-strategy-roadmapping", "fractional-cto", "project-leadership"],
  },
  {
    slug: "modernization-integration",
    name: "Modernization & Integration",
    icon: "RefreshCw",
    summary: "Replace aging systems and connect the ones that should already be talking to each other.",
    services: ["software-modernization", "systems-integration", "cloud-infrastructure"],
  },
  {
    slug: "risk-readiness",
    name: "Risk & Readiness",
    icon: "ShieldCheck",
    summary: "Understand your exposure — from cybersecurity gaps to AI adoption — before it costs you.",
    services: ["cybersecurity-assessment", "ai-readiness"],
  },
  {
    slug: "operations-intelligence",
    name: "Operations & Intelligence",
    icon: "BarChart3",
    summary: "Streamline workflows, trust your data, and choose vendors with confidence.",
    services: ["business-process-automation", "business-analysis", "data-reporting-bi", "vendor-selection"],
  },
];

// ---------- Problems We Solve ----------
